/**
 * Phản hồi của tenant-service (`openapi/platform-api.yaml`, tag `security`) →
 * kiểu màn hình của phân hệ Bảo mật.
 *
 * OpenAPI để vài trường của `EffectiveSetting`, `SecurityPolicyView` không bắt
 * buộc, và `GET /security/password-policy` trả `PasswordPolicy` — hình dạng
 * khác với ba tầng còn lại. Form cài đặt viết một lần cho một hình dạng, nên
 * chuẩn hoá ở đây, ngay sau đường dây, thay vì rải `?? null` khắp view.
 */
import type { BienHieuLuc, MaGoi, MucCaiDat, NguonGiaTri, PhamViCaiDat } from './types.ts';

/** `EffectiveSetting` như trên đường dây: ba trường cuối có thể vắng. */
export interface EffectiveSettingApi {
  key: string;
  value?: unknown;
  source: NguonGiaTri;
  inherited_value?: unknown;
  inherited_source?: NguonGiaTri | null;
  editable: boolean;
  bounds?: BienHieuLuc | null;
  forced_by?: string | null;
}

/** `SecurityPolicyView` như trên đường dây. */
export interface SecurityPolicyViewApi {
  scope: PhamViCaiDat;
  scope_key: string;
  plan_code?: MaGoi | null;
  version: number;
  settings: EffectiveSettingApi[];
  updated_at?: string | null;
  updated_by?: string | null;
  caller_ip?: string | null;
  verified_domains?: string[];
}

/** `PasswordPolicy` — sáu khoá mật khẩu ở tầng nền tảng (ADR-0059 mục 5.2, ADR-0018). */
export interface PasswordPolicyApi {
  version: number;
  settings: EffectiveSettingApi[];
  sync_status: 'SYNCED' | 'PENDING' | 'DISABLED';
  updated_at?: string | null;
  updated_by?: string | null;
}

/** Hình dạng màn hình dùng chung cho bốn nguồn chính sách. */
export interface ChinhSachDaChuanHoa {
  scope: PhamViCaiDat;
  scope_key: string;
  plan_code: MaGoi | null;
  version: number;
  settings: MucCaiDat[];
  updated_at: string | null;
  updated_by: string | null;
  caller_ip?: string | null;
  /** Domain đã xác minh, chỉ ở tầng tenant (ADR-0099); bản API cũ không có thì `undefined`. */
  verified_domains?: string[];
  sync_status?: 'SYNCED' | 'PENDING' | 'DISABLED';
}

/**
 * Một tham số hiệu lực. Không có giá trị kế thừa thì coi như kế thừa mặc định
 * (`DEFAULT`, giá trị `null`): nút "Về mặc định" khi đó gửi `null` và backend
 * tự giải.
 */
export function mucTuApi(m: EffectiveSettingApi): MucCaiDat {
  return {
    key: m.key,
    value: m.value ?? null,
    source: m.source,
    inherited_value: m.inherited_value ?? null,
    inherited_source: m.inherited_source ?? 'DEFAULT',
    editable: m.editable,
    bounds: m.bounds ?? null,
    ...(m.forced_by ? { forced_by: m.forced_by } : {}),
  };
}

export function chinhSachTuApi(v: SecurityPolicyViewApi): ChinhSachDaChuanHoa {
  return {
    scope: v.scope,
    scope_key: v.scope_key,
    plan_code: v.plan_code ?? null,
    version: v.version,
    settings: v.settings.map(mucTuApi),
    updated_at: v.updated_at ?? null,
    updated_by: v.updated_by ?? null,
    caller_ip: v.caller_ip ?? null,
    ...(v.verified_domains ? { verified_domains: v.verified_domains } : {}),
  };
}

/** Chính sách mật khẩu là một lát của tầng nền tảng: `scope = PLATFORM`, `scope_key = "1"`. */
export function matKhauTuApi(p: PasswordPolicyApi): ChinhSachDaChuanHoa {
  return {
    scope: 'PLATFORM',
    scope_key: '1',
    plan_code: null,
    version: p.version,
    settings: p.settings.map(mucTuApi),
    updated_at: p.updated_at ?? null,
    updated_by: p.updated_by ?? null,
    sync_status: p.sync_status,
  };
}

/**
 * Giá trị header `If-Match`. ETag của mọi chính sách là `"<version>"`
 * (`components/headers/ETag`), và `version` đã có trong thân phản hồi — nên
 * dựng lại từ đó thay vì giữ header riêng cạnh dữ liệu đã cache.
 */
export function ifMatchTuPhienBan(version: number): string {
  return `"${version}"`;
}
